import type { SnackbarProps } from '@mui/material';
import type { StorageUnit } from 'store/storageunits/types';

export enum MessageType {
  MOVE = 'MOVE',
  RENAME = 'RENAME',
  UNDO = 'UNDO',
  CREATED = 'CREATED',
  SAVE_AS = 'SAVE_AS',
  DELTED = 'DELTED',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  ACCESS_REMOVED = 'ACCESS_REMOVED',
  ERROR = 'ERROR',
  INFO = 'INFO',
  AGREEMENT_APPROVED = 'AGREEMENT_APPROVED',
  OFFLINE = 'OFFLINE',
  SOFT_LEASE = 'SOFT_LEASE',
  TEMPLATE = 'TEMPLATE',
  MAKE_A_COPY = 'MAKE_A_COPY',
  FILE_SAVED = 'FILE_SAVED',
  FILE_APPROVED = 'FILE_APPROVED',
  ARCHIVED = 'ARCHIVED',
  RESTORED = 'RESTORED',
  ARCHIVED_DELETED = 'ARCHIVED_DELETED',
}

export type ToastScope = 'designer' | 'explorer';

type SnackbarMessage<T extends MessageType, U extends object> = {
  id: string;
  scope: ToastScope;
  closable: boolean;
  type: T;
  title: string;
  data: U;
  error?: boolean;
  permanent?: boolean;
  position?: SnackbarProps['anchorOrigin'];
  duration?: number;
  icon?: React.ElementType;
};

export type MoveSnackbarMessage = SnackbarMessage<
  MessageType.MOVE,
  { unit: StorageUnit; newParentId?: string; oldParentId?: string }
>;

export type RenameSnackbarMessage = SnackbarMessage<
  MessageType.RENAME,
  { unit: StorageUnit; oldUnit: StorageUnit }
>;

export type CreatedSnackbarMessage = SnackbarMessage<
  MessageType.CREATED | MessageType.SAVE_AS,
  { unit: StorageUnit; undoable?: boolean }
>;

export type DeletedSnackbarMessage = SnackbarMessage<
  MessageType.DELTED,
  { unit: StorageUnit }
>;

export type AccessGrantedSnackbarMessage = SnackbarMessage<
  MessageType.ACCESS_GRANTED,
  Record<string, unknown>
>;

export type AccessRemovedSnackbarMessage = SnackbarMessage<
  MessageType.ACCESS_REMOVED,
  Record<string, unknown>
>;

export type ErrorSnackbarMessage = SnackbarMessage<
  MessageType.ERROR,
  Record<string, unknown>
>;
export type InfoSnackbarMessage = SnackbarMessage<
  MessageType.INFO,
  Record<string, unknown>
>;

export type AgreementsApprovedSnackbarMessage = SnackbarMessage<
  MessageType.AGREEMENT_APPROVED,
  Record<string, unknown>
>;

export type ActionUndoneMessage = SnackbarMessage<
  MessageType.UNDO,
  Record<string, unknown>
>;

export type OfflineMessage = SnackbarMessage<
  MessageType.OFFLINE,
  Record<string, unknown>
>;

export type SoftLeaseMessage = SnackbarMessage<
  MessageType.SOFT_LEASE,
  {
    id: string;
    name: string;
  }
>;

export type TemplateMessage = SnackbarMessage<
  MessageType.TEMPLATE,
  { id: string; name: string }
>;

export type MakeACopyMessage = SnackbarMessage<
  MessageType.MAKE_A_COPY,
  {
    id: string;
    name: string;
    lockedBy?: string;
    useConflictDraft?: boolean;
  }
>;

export type FileSavedMessage = SnackbarMessage<
  MessageType.FILE_SAVED,
  Record<string, unknown>
>;
export type FileApprovedMessage = SnackbarMessage<
  MessageType.FILE_APPROVED,
  Record<string, unknown>
>;

export type FileArchivedMessage = SnackbarMessage<
  MessageType.ARCHIVED,
  { id: string }
>;
export type FileRestoredMessage = SnackbarMessage<
  MessageType.RESTORED,
  Record<string, unknown>
>;
export type AllArchivedDeletedMessage = SnackbarMessage<
  MessageType.ARCHIVED_DELETED,
  Record<string, unknown>
>;

export type Message =
  | MoveSnackbarMessage
  | RenameSnackbarMessage
  | CreatedSnackbarMessage
  | DeletedSnackbarMessage
  | AccessGrantedSnackbarMessage
  | AccessRemovedSnackbarMessage
  | AgreementsApprovedSnackbarMessage
  | ErrorSnackbarMessage
  | InfoSnackbarMessage
  | OfflineMessage
  | SoftLeaseMessage
  | TemplateMessage
  | MakeACopyMessage
  | FileSavedMessage
  | FileApprovedMessage
  | FileArchivedMessage
  | FileRestoredMessage
  | FileRestoredMessage
  | AllArchivedDeletedMessage
  | ActionUndoneMessage;

export interface SnackbarReducerInterface {
  messages: Message[];
  permanentMessage: Message[];
}
